<script setup lang="ts">
definePageMeta({ layout: false, colorMode: 'light' })

const route = useRoute()
const tenantSlug = String(route.params.tenantSlug)
const api = usePortalAccountApi(tenantSlug)
const { locale } = useI18n()

const fullName = ref('')
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const loading = ref(false)
const error = ref<string | null>(null)
const done = ref(false)

async function submit() {
  error.value = null
  if (password.value !== passwordConfirmation.value) {
    error.value = 'Passwords do not match.'
    return
  }
  loading.value = true
  try {
    await api.register(email.value, password.value, passwordConfirmation.value, fullName.value, locale.value)
    done.value = true
  } catch (e: unknown) {
    const err = e as { data?: { message?: string, errors?: Record<string, string[]> } }
    error.value = err.data?.errors?.email?.[0] ?? err.data?.errors?.full_name?.[0] ?? err.data?.message ?? 'Could not create your account.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-6">
    <div class="w-full max-w-sm space-y-6">
      <h1 class="text-xl font-semibold text-center">
        Create your client portal account
      </h1>

      <div
        v-if="done"
        class="text-sm text-center space-y-3"
      >
        <p class="text-green-700 bg-green-50 border border-green-200 rounded p-3">
          Check your email for a verification link to finish setting up your account.
        </p>
        <NuxtLink
          :to="`/client/${tenantSlug}/login`"
          class="text-blue-600 hover:underline"
        >
          Back to sign in
        </NuxtLink>
      </div>

      <form
        v-else
        class="space-y-4"
        @submit.prevent="submit"
      >
        <div>
          <label class="block text-sm text-gray-700 mb-1">Full name</label>
          <input
            v-model="fullName"
            type="text"
            required
            class="w-full border rounded px-3 py-2 text-sm"
          >
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Email</label>
          <input
            v-model="email"
            type="email"
            required
            class="w-full border rounded px-3 py-2 text-sm"
          >
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Password</label>
          <input
            v-model="password"
            type="password"
            required
            minlength="8"
            class="w-full border rounded px-3 py-2 text-sm"
          >
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Confirm password</label>
          <input
            v-model="passwordConfirmation"
            type="password"
            required
            minlength="8"
            class="w-full border rounded px-3 py-2 text-sm"
          >
        </div>
        <p
          v-if="error"
          class="text-sm text-red-600"
        >
          {{ error }}
        </p>
        <button
          type="submit"
          :disabled="loading"
          class="w-full bg-black text-white rounded py-2 text-sm disabled:opacity-50"
        >
          {{ loading ? 'Creating…' : 'Create account' }}
        </button>
      </form>

      <p class="text-xs text-gray-500 text-center">
        Already have an account?
        <NuxtLink
          :to="`/client/${tenantSlug}/login`"
          class="text-blue-600 hover:underline"
        >
          Sign in
        </NuxtLink>
      </p>
    </div>
  </div>
</template>
